import { Button } from "@monbits/ui";
import { CheckCheck, Copy, Download } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

interface TwoFactorBackupCodesProps {
	codes: string[];
	onDownload?: () => void;
	onClose?: () => void;
	showWarning?: boolean;
}

export const TwoFactorBackupCodes: React.FC<TwoFactorBackupCodesProps> = ({
	codes,
	onDownload,
	onClose,
	showWarning = true,
}) => {
	const [copied, setCopied] = React.useState(false);

	const handleCopy = () => {
		const text = codes.join("\n");
		navigator.clipboard.writeText(text);
		setCopied(true);
		toast.success("Backup codes copied to clipboard");
		setTimeout(() => setCopied(false), 2000);
	};

	const handleDownload = () => {
		const text = codes.join("\n");
		const blob = new Blob([text], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `monbitspay-backup-codes-${Date.now()}.txt`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
		toast.success("Backup codes downloaded");
		onDownload?.();
	};

	return (
		<div className="space-y-4">
			{showWarning && (
				<div className="p-3 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg">
					<p className="text-sm text-yellow-800 dark:text-yellow-200">
						These codes will not be shown again. Make sure to save them now.
					</p>
				</div>
			)}

			<div className="grid grid-cols-2 gap-2 p-4 bg-muted rounded-lg font-mono text-sm">
				{codes.map((code) => (
					<div key={code} className="py-1">
						{code}
					</div>
				))}
			</div>

			<div className="flex gap-2">
				<Button onClick={handleCopy} variant="outline" className="flex-1">
					{copied ? (
						<>
							<CheckCheck className="mr-2 h-4 w-4" />
							Copied
						</>
					) : (
						<>
							<Copy className="mr-2 h-4 w-4" />
							Copy
						</>
					)}
				</Button>

				<Button onClick={handleDownload} variant="outline" className="flex-1">
					<Download className="mr-2 h-4 w-4" />
					Download
				</Button>
			</div>

			{onClose && (
				<Button onClick={onClose} className="w-full">
					Continue
				</Button>
			)}
		</div>
	);
};
